import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, BehaviorSubject, firstValueFrom, from } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { MyPermissionsService } from './my-permissions.service';
import { Tokens } from '../models/auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly tokenStorageKey = 'token';
  private readonly refreshLockStorageKey = 'token_refresh_lock';
  private readonly refreshLeadTimeMs = 60000;
  private readonly refreshLockTtlMs = 30000;
  private readonly tabId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  constructor(
    private http: HttpClient,
    private router: Router,
    private myPermissions: MyPermissionsService
  ) {
    window.addEventListener('storage', this.handleStorageEvent);
  }
  user = new BehaviorSubject<any>(null);
  refreshTimeout?: ReturnType<typeof setTimeout>;

  signIn(data: {
    email: string;
    password: string;
    current2FaCode: string | null;
  }): Observable<Tokens> {
    return this.http.post<Tokens>(`/tokens`, data).pipe(
      tap((resp: Tokens) => {
        this.handleAuth(resp);
      }),
    );
  }

  autoSignIn() {
    const token = this.getStoredTokens();
    const withRedirect = window.location.pathname === '/';

    if (!token) {
      return;
    }

    this.applyAuthState(token, withRedirect);

    if (this.shouldRefreshToken(token.token)) {
      this.refreshTokenWithCoordination(token).subscribe({
        next: (resp: Tokens) => {
          this.handleAuth(resp, false);
        },
        error: () => {
          this.handleRefreshFailure(token);
        },
      });
    }
  }

  private setUserFromStorage(tokens: Tokens) {
    const jwtHelper = new JwtHelperService();
    const userData = jwtHelper.decodeToken(tokens.token);

    this.user.next(userData);
  }

  private handleAuth(data: Tokens, withRedirect = true) {
    localStorage.setItem(this.tokenStorageKey, JSON.stringify(data));
    this.applyAuthState(data, withRedirect);
  }

  /**
   * ECDEV-256: adopt a token minted by POST /tokens/switch-company.
   *
   * Writing to `token` is also what the cross-tab `storage` listener keys off, so every other tab
   * picks the new company up on its own. The caller reloads afterwards — nothing here tries to
   * re-derive in-memory state.
   */
  applySwitchedTokens(tokens: Tokens) {
    this.handleAuth(tokens, false);
  }

  refreshToken(tokens: {
    token: string;
    refreshToken: string;
  }): Observable<Tokens> {
    return this.http.post<Tokens>(`/tokens/refresh`, {
      token: tokens.token,
      refreshToken: tokens.refreshToken,
    });
  }

  refreshTokenWithCoordination(tokens: Tokens): Observable<Tokens> {
    return from(this.coordinateRefresh(tokens));
  }

  private startRefreshTimer(expTime: number, tokens: Tokens) {
    clearTimeout(this.refreshTimeout);

    const expires = new Date(expTime);

    const timeout = Math.max(
      expires.getTime() - Date.now() - this.refreshLeadTimeMs,
      0,
    );
    this.refreshTimeout = setTimeout(
      () =>
        this.refreshTokenWithCoordination(tokens).subscribe({
          next: (resp) => {
            this.handleAuth(resp, false);
          },
          error: () => {
            this.handleRefreshFailure(tokens);
          },
        }),
      timeout,
    );
  }

  getTokenData() {
    return localStorage.getItem(this.tokenStorageKey);
  }

  getUserRole(): string {
    const jwtHelper = new JwtHelperService();
    const token = this.getStoredTokens();
    if (token) {
      const userData = jwtHelper.decodeToken(token.token);
      return userData['base.role'];
    }
    return '';
  }

  public get userData() {
    return this.user.value;
  }

  logout(syncTabs = true) {
    clearTimeout(this.refreshTimeout);
    this.releaseRefreshLock();
    if (syncTabs) {
      localStorage.removeItem(this.tokenStorageKey);
    }

    // ECDEV-256: the grid's saved columns and filters are company-shaped and used to survive
    // logout, so on a shared machine the next user inherited them.
    this.clearCompanyScopedStorage();

    // Same reasoning for the permission list: it is per-user, so it must not outlive the session.
    this.myPermissions.clear();

    this.user.next(null);
    this.router.navigate(['']);
  }

  /**
   * Kept here rather than imported from CompanyScopeService: that service depends on this one, and
   * logout must not pull a cycle in.
   */
  private clearCompanyScopedStorage() {
    const prefixes = [
      'table',
      'reports_table',
      'tc_saved_orders',
      'tc_saved_reports',
      'multiFO',
      'multiFR',
    ];

    Object.keys(localStorage)
      .filter((key) =>
        prefixes.some((p) => key === p || key.startsWith(`${p}:`)),
      )
      .forEach((key) => localStorage.removeItem(key));
  }

  private applyAuthState(tokens: Tokens, withRedirect = false) {
    const jwtHelper = new JwtHelperService();
    const userData = jwtHelper.decodeToken(tokens.token);

    this.startRefreshTimer(userData.exp * 1000, tokens);
    this.user.next(userData);

    if (withRedirect) {
      this.router.navigate(['/dashboard/orders']);
    }
  }

  private shouldRefreshToken(token: string) {
    const jwtHelper = new JwtHelperService();
    const expirationDate = jwtHelper.getTokenExpirationDate(token);

    if (!expirationDate) {
      return true;
    }

    return expirationDate.getTime() - Date.now() <= this.refreshLeadTimeMs;
  }

  private handleRefreshFailure(tokens: Tokens) {
    const latestTokens = this.getStoredTokens();

    if (latestTokens && !this.areTokensEqual(latestTokens, tokens)) {
      this.applyAuthState(latestTokens);
      return;
    }

    this.logout();
  }

  private async coordinateRefresh(tokens: Tokens): Promise<Tokens> {
    const currentTokens = this.getStoredTokens() ?? tokens;

    if (!this.acquireRefreshLock()) {
      const updatedTokens = await this.waitForTokensUpdate(currentTokens);

      if (updatedTokens) {
        return updatedTokens;
      }

      if (!this.acquireRefreshLock()) {
        const syncedTokens = this.getStoredTokens();

        if (syncedTokens && !this.areTokensEqual(syncedTokens, currentTokens)) {
          return syncedTokens;
        }
      }
    }

    const refreshTokens = this.getStoredTokens() ?? currentTokens;

    try {
      return await firstValueFrom(this.refreshToken(refreshTokens));
    } finally {
      this.releaseRefreshLock();
    }
  }

  private acquireRefreshLock() {
    const activeLock = this.getRefreshLock();

    if (
      activeLock &&
      activeLock.owner !== this.tabId &&
      !this.isRefreshLockExpired(activeLock)
    ) {
      return false;
    }

    const nextLock = {
      owner: this.tabId,
      expiresAt: Date.now() + this.refreshLockTtlMs,
    };

    localStorage.setItem(this.refreshLockStorageKey, JSON.stringify(nextLock));

    return this.getRefreshLock()?.owner === this.tabId;
  }

  private releaseRefreshLock() {
    const activeLock = this.getRefreshLock();

    if (!activeLock || activeLock.owner === this.tabId) {
      localStorage.removeItem(this.refreshLockStorageKey);
    }
  }

  private waitForTokensUpdate(previousTokens: Tokens): Promise<Tokens | null> {
    return new Promise((resolve) => {
      let settled = false;

      const finalize = (tokens: Tokens | null) => {
        if (settled) {
          return;
        }

        settled = true;
        window.removeEventListener('storage', onStorage);
        clearTimeout(timeoutId);
        resolve(tokens);
      };

      const readUpdatedTokens = () => {
        const latestTokens = this.getStoredTokens();

        if (
          latestTokens &&
          !this.areTokensEqual(latestTokens, previousTokens)
        ) {
          finalize(latestTokens);
          return;
        }

        const activeLock = this.getRefreshLock();

        if (!activeLock || this.isRefreshLockExpired(activeLock)) {
          finalize(null);
        }
      };

      const onStorage = (event: StorageEvent) => {
        if (
          event.key === this.tokenStorageKey ||
          event.key === this.refreshLockStorageKey
        ) {
          readUpdatedTokens();
        }
      };

      const timeoutId = window.setTimeout(
        () => finalize(null),
        this.refreshLockTtlMs + 1000,
      );

      window.addEventListener('storage', onStorage);
      readUpdatedTokens();
    });
  }

  private getStoredTokens(): Tokens | null {
    return this.parseTokens(localStorage.getItem(this.tokenStorageKey));
  }

  private parseTokens(tokens: string | null): Tokens | null {
    if (!tokens) {
      return null;
    }

    try {
      return JSON.parse(tokens) as Tokens;
    } catch {
      return null;
    }
  }

  private getRefreshLock(): { owner: string; expiresAt: number } | null {
    const refreshLock = localStorage.getItem(this.refreshLockStorageKey);

    if (!refreshLock) {
      return null;
    }

    try {
      return JSON.parse(refreshLock) as { owner: string; expiresAt: number };
    } catch {
      return null;
    }
  }

  private isRefreshLockExpired(lock: { owner: string; expiresAt: number }) {
    return lock.expiresAt <= Date.now();
  }

  private areTokensEqual(left: Tokens, right: Tokens) {
    return (
      left.token === right.token && left.refreshToken === right.refreshToken
    );
  }

  private handleStorageEvent = (event: StorageEvent) => {
    if (event.key !== this.tokenStorageKey) {
      return;
    }

    const tokens = this.parseTokens(event.newValue);

    if (tokens) {
      this.applyAuthState(tokens);
      return;
    }

    clearTimeout(this.refreshTimeout);
    this.user.next(null);
    this.router.navigate(['']);
  };
}
